import * as React from 'react';
import { useDebouncedValue } from '../../../../hooks/use-debounced-value';
import type {
	IOption,
	IUseSelectSearchProps,
	IUseSelectSearchResult,
	SelectOptionValue,
} from '../types';

const DEFAULT_DEBOUNCE_MS = 0;

const defaultFilterOption = <TValue extends SelectOptionValue, TMetadata>(
	option: IOption<TValue, TMetadata>,
	query: string
): boolean => option.label.toLowerCase().includes(query.trim().toLowerCase());

/**
 * Manages controlled or local search state, debounce projection, and in-memory option filtering.
 */
export const useSelectSearch = <TValue extends SelectOptionValue = SelectOptionValue, TMetadata = unknown>({
	debounceMs = DEFAULT_DEBOUNCE_MS,
	defaultSearchValue = '',
	filterOption,
	onSearchChange,
	options,
	searchValue: controlledSearchValue,
}: IUseSelectSearchProps<TValue, TMetadata>): IUseSelectSearchResult<TValue, TMetadata> => {
	const [internalSearchValue, setInternalSearchValue] = React.useState(defaultSearchValue);
	const isSearchControlled = controlledSearchValue !== undefined;
	const searchValue = controlledSearchValue ?? internalSearchValue;
	const resolvedDebounceMs = Math.max(DEFAULT_DEBOUNCE_MS, debounceMs);
	const hasDebounce = resolvedDebounceMs > DEFAULT_DEBOUNCE_MS;
	const debouncedValue = useDebouncedValue(searchValue, resolvedDebounceMs);
	const debouncedSearchValue = hasDebounce ? debouncedValue : searchValue;
	const hasMountedRef = React.useRef(false);
	const onSearchChangeRef = React.useRef(onSearchChange);
	const skipNextDebouncedSearchValueRef = React.useRef<string>();

	React.useEffect(() => {
		onSearchChangeRef.current = onSearchChange;
	}, [onSearchChange]);

	const setSearchValue = React.useCallback(
		(nextSearchValue: string) => {
			if (!isSearchControlled) {
				setInternalSearchValue(nextSearchValue);
			}
			if (isSearchControlled && hasDebounce) {
				skipNextDebouncedSearchValueRef.current = nextSearchValue;
				onSearchChangeRef.current?.(nextSearchValue);
				return;
			}
			if (!hasDebounce) {
				onSearchChange?.(nextSearchValue);
			}
		},
		[hasDebounce, isSearchControlled, onSearchChange]
	);

	React.useEffect(() => {
		if (!hasDebounce || !hasMountedRef.current) {
			hasMountedRef.current = true;
			return;
		}
		if (skipNextDebouncedSearchValueRef.current === debouncedSearchValue) {
			skipNextDebouncedSearchValueRef.current = undefined;
			return;
		}
		onSearchChangeRef.current?.(debouncedSearchValue);
	}, [debouncedSearchValue, hasDebounce]);

	const clearSearch = React.useCallback(() => {
		if (searchValue.length > 0) {
			if (!isSearchControlled) {
				setInternalSearchValue('');
			}
			if (hasDebounce) {
				if (debouncedSearchValue !== '') {
					skipNextDebouncedSearchValueRef.current = '';
				}
				onSearchChangeRef.current?.('');
				return;
			}
			onSearchChange?.('');
		}
	}, [debouncedSearchValue, hasDebounce, isSearchControlled, onSearchChange, searchValue.length]);

	const filteredOptions = React.useMemo(() => {
		if (searchValue.trim().length === 0) {
			return options;
		}
		return options.filter(option =>
			filterOption ? filterOption(option, searchValue) : defaultFilterOption(option, searchValue)
		);
	}, [filterOption, options, searchValue]);

	return {
		clearSearch,
		debouncedSearchValue,
		filteredOptions,
		searchValue,
		setSearchValue,
	};
};
